/**
 * JSON-LD structured data builder helpers for SEO.
 * Returns plain objects to be serialized and injected by the SEO component.
 */

export function organizationSchema({
  name,
  url,
  logo,
  description,
}: {
  name: string;
  url: string;
  logo?: string;
  description?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name,
    url,
    ...(logo && { logo }),
    ...(description && { description }),
  };
}

export function websiteSchema({ name, url }: { name: string; url: string }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name,
    url,
  };
}

export function blogPostingSchema({
  title,
  description,
  author,
  datePublished,
  url,
  image,
}: {
  title: string;
  description: string;
  author: string;
  datePublished: string;
  url: string;
  image?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description,
    author: { '@type': 'Person', name: author },
    datePublished,
    url,
    ...(image && { image }),
  };
}

export function breadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function faqSchema(items: { question: string; answer: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

export function localBusinessSchema({
  name,
  url,
  logo,
  description,
  address,
  areaServed,
}: {
  name: string;
  url: string;
  logo?: string;
  description?: string;
  address?: { streetAddress?: string; addressLocality: string; addressRegion: string; postalCode?: string; addressCountry?: string };
  areaServed?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name,
    url,
    ...(logo && { logo }),
    ...(description && { description }),
    ...(address && {
      address: {
        '@type': 'PostalAddress',
        ...address,
      },
    }),
    ...(areaServed && { areaServed }),
  };
}
